import { useMemo } from 'react';
import { useGetConfigurationQuery } from '@/app/api/resourceApi';
import { useGetRestaurantIdentityQuery } from '@/app/api/hubApi';
import { channelOfMode, isChannelPaused } from '@/features/restaurant/pausedChannels';
import { useGetDeliveryConfigQuery } from './deliveryApi';
import type { OrderMode } from '@/types';

export interface AllowedModes {
  modes: OrderMode[];
  /**
   * `false` while either backend answer is still missing. Callers must not act
   * on `modes` being short while this is `false` — it is the "we don't know yet"
   * list, not a verdict.
   */
  resolved: boolean;
}

/**
 * SC-570: "la livraison est possible" only holds when BOTH backends agree —
 * resource_api's `enable_livraison` (what `config.modes` carries) AND sale_api's
 * DeliveryConfig.enabled (what actually produces a quote; without it the order
 * POST comes back 400 DELIVERY_DISABLED). The cart read only the first, so a
 * restaurant with livraison activee but no delivery zone configured offered
 * "Livraison" in the drawer and had it silently swapped for "A emporter" one
 * screen later. Both surfaces derive their modes here so they cannot drift.
 *
 * SC-570 (retest): agreeing on the same source was not enough, because the hook
 * still answered "delivery is possible" BEFORE either backend had spoken. The
 * mode row therefore rendered "Livraison" on first paint for every restaurant
 * and only lost it once sale_api replied — measured at 7.2 s on preprod's NIHON
 * SUSHI, whose heavy menu keeps the main thread busy long after the response
 * lands. That window is the reported bug in full: the customer picks Livraison,
 * continues, and the checkout has taken it away. So the unknown state now fails
 * CLOSED — a mode is offered only once it is known to be orderable. Widening the
 * list later is harmless; retracting a choice the customer already made is not.
 *
 * An outright request failure still fails OPEN: we cannot tell, sale_api rejects
 * the order anyway, and a transient hiccup must not strip delivery from a
 * correctly configured restaurant.
 *
 * Both queries are read through `currentData`, NOT `data`: `data` keeps the
 * previous argument's result while the new one is in flight, so a drawer that
 * survives a restaurant change would briefly derive restaurant B's modes from
 * restaurant A's answers.
 *
 * SC-1434: the owner's pause is per channel. A paused mode leaves the list the
 * same way an unconfigured delivery does, so the cart, the mode chooser and the
 * checkout fall back to the mode that is still open.
 */
export function useAllowedModes(restaurantUuid: string): AllowedModes {
  const { currentData: config, isError: configFailed } = useGetConfigurationQuery(restaurantUuid, {
    skip: !restaurantUuid,
  });
  const { currentData: deliveryConfig, isError: deliveryFailed } = useGetDeliveryConfigQuery(
    restaurantUuid,
    { skip: !restaurantUuid },
  );

  const { currentData: identity } = useGetRestaurantIdentityQuery(restaurantUuid, {
    skip: !restaurantUuid,
  });

  const configKnown = config !== undefined || Boolean(configFailed);
  const deliveryKnown = deliveryConfig !== undefined || Boolean(deliveryFailed);
  const resolved = configKnown && deliveryKnown;
  const deliveryEnabled = deliveryConfig?.enabled ?? true;

  return useMemo<AllowedModes>(() => {
    const base = config?.modes ?? ['takeaway', 'delivery'];
    const offerDelivery = resolved && deliveryEnabled;
    const offered = offerDelivery ? base : base.filter((m) => m !== 'delivery');
    return {
      modes: identity ? offered.filter((m) => !isChannelPaused(identity, channelOfMode(m))) : offered,
      resolved,
    };
  }, [config?.modes, deliveryEnabled, resolved, identity]);
}
